/*
 * ====================================================================================================
 * WARNING: BACKEND-COPIED DTO FILE
 * THIS FILE IS COPIED FROM THE BACKEND.
 * DO NOT MODIFY THIS FILE MANUALLY IN FRONTEND CODE.
 * IT MAY BE DELETED, REGENERATED, OR REPLACED AT ANY TIME.
 * ====================================================================================================
 */

/**
 * GTFS location_type values (stops.txt).
 */
export enum StopLocationType {
    "StopOrPlatform" = 0,
    "Station" = 1,
    "EntranceExit" = 2,
    "GenericNode" = 3,
    "BoardingArea" = 4,
}

/**
 * GTFS wheelchair_boarding values (stops.txt).
 */
export enum StopWheelchairBoarding {
    "NoInformation" = 0,
    "Accessible" = 1,
    "NotAccessible" = 2,
}

/**
 * Filters bbox results to parent or child spatial index only.
 */
export enum StopsBboxStopKind {
    "Parent" = "parent",
    "Child" = "child",
}

/**
 * Full GTFS stop payload returned by the spatial index and GET /api/stops.
 */
export class StopDto {

    /**
     * Prefixed GTFS stop id (`{gtfs_source}_{feed_stop_id}`)
     * Example: tpg_8508128:0:J
     */
    stopId!: string;

    /**
     * GTFS feed source identifier
     * Example: tpg
     * Nullable: true
     */
    gtfsSource!: string | null;

    /**
     * Public stop name
     * Nullable: true
     */
    stopName!: string | null;

    /**
     * Latitude (WGS84)
     * Example: 46.204
     */
    stopLat!: number;

    /**
     * Longitude (WGS84)
     * Example: 6.143
     */
    stopLon!: number;

    /**
     * Latitude delta (WGS84 degrees) added at map display time. Display latitude is `stopLat + displayLatDelta`.
     * Example: 0
     */
    displayLatDelta!: number;

    /**
     * Longitude delta (WGS84 degrees) added at map display time. Display longitude is `stopLon + displayLonDelta`.
     * Example: 0
     */
    displayLonDelta!: number;

    /**
     * Rider-facing stop code
     * Nullable: true
     */
    stopCode!: string | null;

    /**
     * Stop description
     * Nullable: true
     */
    stopDesc!: string | null;

    /**
     * Fare zone id
     * Nullable: true
     */
    zoneId!: string | null;

    /**
     * Stop web page URL
     * Nullable: true
     */
    stopUrl!: string | null;

    /**
     * GTFS location type
     * Enum: StopLocationType
     * Nullable: true
     */
    locationType!: StopLocationType | null;

    /**
     * Parent station id from GTFS (unprefixed feed value)
     * Nullable: true
     */
    parentStation!: string | null;

    /**
     * Stop timezone
     * Nullable: true
     */
    stopTimezone!: string | null;

    /**
     * Wheelchair boarding accessibility
     * Enum: StopWheelchairBoarding
     * Nullable: true
     */
    wheelchairBoarding!: StopWheelchairBoarding | null;

    /**
     * Level id (levels.txt)
     * Nullable: true
     */
    levelId!: string | null;

    /**
     * Platform code visible to riders
     * Nullable: true
     */
    platformCode!: string | null;

    /** True when this stop is indexed as a parent station */
    isParent!: boolean;

    /** True when this stop is indexed in the child spatial index (stop has at least one stop_time row) */
    isChild!: boolean;

    /**
     * Prefixed GTFS stop ids of child platforms indexed under this parent station. Excludes the parent stop itself when it is also indexed as a child. Empty when the stop is not a parent or has no other indexed child platforms.
     * Example: [ "tpg_8508128:0:J", "tpg_8508128:0:K" ]
     * Type: String
     */
    childStopIds!: string[];

}

/**
 * Default grouped bbox response (parents and children arrays).
 */
export class StopsBboxGroupedResponseDto {

    /**
     * Parent stops in the bounding box
     * Type: StopDto
     */
    parents!: StopDto[];

    /**
     * Child-indexed stops in the bounding box (stops with at least one stop_time row)
     * Type: StopDto
     */
    children!: StopDto[];

}

/**
 * Response body for POST /api/stops/rebuild-index.
 */
export class StopsIndexRebuildResponseDto {

    /** Number of stops in the parent spatial index */
    parentCount!: number;

    /** Number of stops in the child spatial index */
    childCount!: number;

    /** Estimated in-memory size of the parent index (bytes, heuristic) */
    parentIndexEstimatedBytes!: number;

    /** Estimated in-memory size of the child index (bytes, heuristic) */
    childIndexEstimatedBytes!: number;

    /** Wall-clock rebuild duration in milliseconds */
    durationMs!: number;

}
